import componentModel from 'decorators/componentModel';
import { inject, injectable } from "inversify";
import { PureComputed, Subscribable, SubscribableOrNullableValue, SubscribableOrValue } from "knockout";
import { wccModules } from "../../../enums/wccModules";
import './base.scss';

export interface DropdownItem<T = any> {
    title: Subscribable<string> | string
    value: T
    header?: SubscribableOrNullableValue<string>
    separate?: SubscribableOrNullableValue<boolean>
    separateAfter?: SubscribableOrNullableValue<boolean>
}

export interface JSONDropdown<T = any> {
    Items?: SubscribableOrValue<Array<DropdownItem<T>>>
    items?: SubscribableOrValue<Array<DropdownItem<T>>>
    theme?: DropdownTheme
    textSize?: DropdownTextSize
}

export type DropdownItemOrPredicate<T = any> = DropdownItem<T> | ((item: DropdownItem<T>) => boolean)
export type DropdownTheme = 'darkgrey' | 'black' | 'blue' | 'grey' | 'white';
export type DropdownTextSize = 'medium' | 'small' | 'extrasmall' | 'large';

export abstract class Dropdown<T = any> {
    items: PureComputed<Array<DropdownItem<T>>>
    theme: Subscribable<DropdownTheme>
    textSize: Subscribable<DropdownTextSize>

    constructor(jsonModel?: JSONDropdown<T>) {
        this.items = ko.observable(jsonModel?.Items ?? jsonModel?.items).unwrap().default([]);
        this.theme = ko.flattenComputed(jsonModel?.theme, 'darkgrey');
        this.textSize = ko.flattenComputed(jsonModel?.textSize, 'small');
    }

    protected findItem(itemOrPredicate: DropdownItemOrPredicate<T>) {
        if (_.isFunction(itemOrPredicate))
            return this.items().find(itemOrPredicate);
        else if (_.isObject(itemOrPredicate) && this.items().includes(itemOrPredicate))
            return itemOrPredicate;

        return undefined;
    }
}

@injectable()
@componentModel(Dropdown)
export default class DropdownVM<T = any> {
    bodyNode = ko.observable<HTMLElement>();

    constructor(
        @inject(wccModules.componentConfig) model: Dropdown<T>,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo
    ) {
        const isAlwaysShown = ko.pureComputed(() => this.checkIfAlwaysShown());
        const isVisible = ko.pureComputed(() => isAlwaysShown() || model.items().length > 0);

        ko.applyBindingsToNode(info.element, {
            'visible': isVisible,

            'css': {
                'wcc-dropdown': true,
                'wcc-dropdown--black': model.theme.is('black'),
                'wcc-dropdown--blue': model.theme.is('blue'),
                'wcc-dropdown--grey': model.theme.is('grey'),
                'wcc-dropdown--white': model.theme.is('white'),
                'wcc-dropdown--text-m': model.textSize.is('medium'),
                'wcc-dropdown--text-xs': model.textSize.is('extrasmall'),
                'wcc-dropdown--text-lg': model.textSize.is('large')
            }
        });
    }

    onBodyClick = (m: any, e: JQueryEventObject) => {
        const $target = $(e.target);
        const $rowOrBody = $target.closest('li, .dropdown-menu');

        let isClickable = $rowOrBody.is('li') && $rowOrBody.children('a').length > 0;

        if (!isClickable) {
            e.preventDefault();
            e.stopPropagation();
        }
    }

    protected checkIfAlwaysShown() {
        return false;
    }
}